import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Auth.css';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signup, loginWithGoogle, processingRedirect } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get the redirect path - check URL param first, then sessionStorage, then location state
  const getRedirectPath = () => {
    // First check URL parameter (most reliable)
    const urlRedirect = searchParams.get('redirect');
    if (urlRedirect) {
      return decodeURIComponent(urlRedirect);
    }
    // Then check sessionStorage (for Google redirect flow)
    const storedPath = sessionStorage.getItem('redirectAfterLogin');
    if (storedPath) {
      return storedPath;
    }
    // Finally check location state
    return location.state?.from?.pathname || '/dashboard';
  };

  const redirectPath = getRedirectPath();

  // Add auth-page class to body for gradient background
  useEffect(() => {
    document.body.classList.add('auth-page-body');
    return () => {
      document.body.classList.remove('auth-page-body');
    };
  }, []);

  // Show loading while processing redirect
  if (processingRedirect) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>جاري تسجيل الدخول...</p>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>يرجى الانتظار...</p>
          </div>
        </div>
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!email || !password || !displayName) {
      return setError('يرجى ملء جميع الحقول');
    }

    if (password !== confirmPassword) {
      return setError('كلمات المرور غير متطابقة');
    }

    if (password.length < 8) {
      return setError('يجب أن تكون كلمة المرور 8 أحرف على الأقل');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password, displayName);
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use' || (err.message && err.message.includes('email-already-in-use'))) {
        setError('هذا البريد الإلكتروني مستخدم بالفعل. يرجى تسجيل الدخول أو استخدام بريد آخر.');
      } 
      else if (err.code === 'auth/invalid-email' || (err.message && err.message.includes('invalid-email'))) {
        setError('البريد الإلكتروني غير صالح.');
      } 
      else {
        setError('فشل إنشاء الحساب. يرجى المحاولة مرة أخرى.');
      }
    }

    setLoading(false);
  }

  async function handleGoogleSignup() {
    try {
      setError('');
      setLoading(true);

      // Store the redirect path for after Google login (in case of redirect flow)
      sessionStorage.setItem('redirectAfterLogin', redirectPath);

      await loginWithGoogle();

      // For popup login, navigate immediately
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error(err);
      setError('فشل التسجيل بواسطة Google: ' + err.message);
      setLoading(false);
    }
  }

  return (
    <div className="auth-container signup-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/Clogo192.png" alt="Calendar" />
        </div>
        <h2 className="arabic">إنشاء حساب</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>الاسم الكامل (اللقب)</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="أدخل اسمك"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>البريد الإلكتروني</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="أدخل بريدك الإلكتروني"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>كلمة المرور</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8 أحرف على الأقل"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>تأكيد كلمة المرور</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="أعد إدخال كلمة المرور"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
          </button>
        </form>

        <div className="divider">
          <span>أو</span>
        </div>

        <button
          onClick={handleGoogleSignup}
          className="btn btn-google btn-block"
          disabled={loading}
        >
          {loading ? 'جاري التحويل...' : 'المتابعة بواسطة Google'}
        </button>

        <p className="auth-footer">
          لديك حساب بالفعل؟ <Link to={redirectPath !== '/dashboard' ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login'}>تسجيل الدخول</Link>
        </p>
      </div>
    </div>
  );
}